import { normalizeStringArray } from './compatibilityOptions';
import { getPinCapabilityTags, PIN_CAPABILITY_COLORS } from './pinFilterGroups';
const POWER_CALLOUT_LABELS = new Set([
  'VIN',
  'RAW',
  '5V',
  'VCC',
  'VDD',
  '3V3',
]);
const GROUND_CALLOUT_LABELS = new Set([
  'GND',
  'GROUND',
  'AGND',
  'PGND',
  'GNDA',
  'GNDD',
]);

const isDomElement = (value) => (
  typeof Element !== 'undefined'
  && value instanceof Element
);

const isGroundCalloutLabel = (label) => (
  GROUND_CALLOUT_LABELS.has(label)
  || /^(?:A?P?GND|GND\d+|GROUND\d*)$/.test(label)
);

const isPowerCalloutLabel = (label) => (
  POWER_CALLOUT_LABELS.has(label)
  || /^(?:VCC|VDD|VIN)\d*$/.test(label)
);

const normalizeQuickPinPowerLabel = (value) => {
  const upperValue = String(value || '').trim().toUpperCase();

  if (upperValue === '3.3V') {
    return '3V3';
  }

  if (upperValue === 'GROUND') {
    return 'GND';
  }

  return upperValue;
};

const setSvgStyle = (element, propertyName, value) => {
  if (!isDomElement(element)) {
    return;
  }

  if (String(value ?? '').trim()) {
    element.style.setProperty(propertyName, String(value));
    return;
  }

  element.style.removeProperty(propertyName);
};

const getPinFieldValue = (pin, fieldNames) => {
  for (const fieldName of fieldNames) {
    const value = pin?.[fieldName];
    if (value !== undefined && value !== null) {
      const normalizedValue = String(value).trim();
      if (normalizedValue) {
        return normalizedValue;
      }
    }
  }

  return '';
};

const doesCalloutGroupMatchPin = (calloutGroup, pin) => {
  if (!isDomElement(calloutGroup) || !pin) {
    return false;
  }

  const identityElement = calloutGroup.querySelector('[data-pin], [data-pin-number], [data-svgid]');
  if (!isDomElement(identityElement)) {
    return false;
  }

  const svgId = String(identityElement.getAttribute('data-svgid') || '').trim();
  const pinSvgId = getPinFieldValue(pin, ['svg_id', 'svgId']);
  if (svgId && pinSvgId && svgId === pinSvgId) {
    return true;
  }

  const pinNumber = String(identityElement.getAttribute('data-pin-number') || '').trim();
  const candidatePinNumber = getPinFieldValue(pin, ['pin_number', 'pinNumber']);
  if (pinNumber && candidatePinNumber && pinNumber === candidatePinNumber) {
    return true;
  }

  const pinName = String(identityElement.getAttribute('data-pin') || '').trim();
  const candidatePinName = getPinFieldValue(pin, ['pin_name', 'pinName']);
  return Boolean(pinName && candidatePinName && pinName === candidatePinName);
};

const labelMatches = (label, pattern) => pattern.test(String(label || '').trim().toUpperCase());

export const getPublicPinCalloutCapability = (pin, calloutLabel) => {
  if (!pin) {
    return 'special';
  }

  const pinRole = String(pin?.pin_role ?? pin?.pinRole ?? '').trim().toLowerCase();
  const capabilityTags = getPinCapabilityTags(pin);
  const alternateNames = normalizeStringArray(pin?.alternate_pin_names ?? pin?.alternatePinNames);
  const resolvedCalloutLabel = String(calloutLabel ?? pin?.pin_name ?? pin?.pinName ?? '').trim();
  const pinLabels = normalizeStringArray([
    pin?.pin_name,
    pin?.pin_number,
    ...alternateNames,
  ]).map((label) => normalizeQuickPinPowerLabel(label));
  const hasTag = (...tags) => capabilityTags.some((tag) => tags.includes(tag));
  const hasTagPrefix = (prefix) => capabilityTags.some((tag) => tag === prefix || tag.startsWith(`${prefix}-`));

  const isGroundPin = (
    pinRole === 'ground'
    || hasTag('ground', 'gnd')
    || pinLabels.some((label) => isGroundCalloutLabel(label))
  );

  if (isGroundPin) {
    return 'ground';
  }

  const isPowerPin = (
    pinRole.startsWith('power')
    || hasTagPrefix('power')
    || pinLabels.some((label) => isPowerCalloutLabel(label))
  );

  if (isPowerPin) return 'power';

  // A callout label identifies which of a multi-capability pin's functions it
  // represents. This keeps GPIO21 blue while its SDA alternate is purple.
  // Prefer an explicit protocol prefix before generic signal names: RX/TX can
  // be SPI directions too (for example, RP2350 labels such as SPI0-RX).
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])SPI\d*(?:[-_/ ]?(?:MOSI|MISO|SCK|SCLK|CSN?|SS|TX|RX))?(?:[^A-Z0-9]|$)/)) return 'spi';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])I2C\d*(?:[-_/ ]?(?:SDA|SCL))?(?:[^A-Z0-9]|$)/)) return 'i2c';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])UART\d*(?:[-_/ ]?(?:TX|RX|CTS|RTS))?(?:[^A-Z0-9]|$)/)) return 'uart';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])(?:UART|TX|RX)\d*(?:[^A-Z0-9]|$)/)) return 'uart';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])(?:I2C|IIC|SDA|SCL)\d*(?:[^A-Z0-9]|$)/)) return 'i2c';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])(?:SPI|MOSI|MISO|SCK|SCLK|CSN?|SS)(?:[^A-Z0-9]|$)/)) return 'spi';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])PWM\d*(?:[^A-Z0-9]|$)/)) return 'pwm';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])DAC\d*(?:[^A-Z0-9]|$)/)) return 'dac';
  if (labelMatches(resolvedCalloutLabel, /^(?:A\d+|ADC\d*)$/)) return 'analog';
  if (labelMatches(resolvedCalloutLabel, /(?:^|[^A-Z0-9])(?:RST|RESET|EN|ENABLE|BOOT|CTRL|CONTROL)(?:[^A-Z0-9]|$)/)) return 'control';
  if (labelMatches(resolvedCalloutLabel, /^(?:GPIO|PIO|IO|D)\d+$/)) return 'gpio';

  if (pinRole.startsWith('uart') || hasTagPrefix('uart')) return 'uart';
  if (pinRole.startsWith('i2c') || hasTagPrefix('i2c')) return 'i2c';
  if (pinRole.startsWith('spi') || hasTagPrefix('spi')) return 'spi';
  if (pinRole === 'pwm' || hasTagPrefix('pwm')) return 'pwm';
  if (pinRole.startsWith('analog-out') || hasTag('dac')) return 'dac';
  if (pinRole.startsWith('analog') || hasTagPrefix('analog') || hasTag('adc')) return 'analog';
  if (pinRole === 'gpio' || hasTag('gpio', 'pio', 'digital', 'digital-in', 'digital-input', 'digital-out', 'digital-output')) return 'gpio';
  if (pinRole === 'control' || hasTag('control', 'chip-select', 'chip-enable', 'enable', 'reset', 'clock', 'boot', 'interrupt')) return 'control';
  return 'special';
};

export const getDefaultPublicPinCalloutColor = (pin, calloutLabel) => (
  PIN_CAPABILITY_COLORS[getPublicPinCalloutCapability(pin, calloutLabel)]
  || PIN_CAPABILITY_COLORS.special
);

export const applyDefaultPublicPinoutCalloutColors = (svg, pins = []) => {
  if (!isDomElement(svg)) {
    return false;
  }

  const normalizedPins = Array.isArray(pins) ? pins : [];
  const calloutGroups = Array.from(svg.querySelectorAll('g[data-micropinouts-callout="1"]'));

  calloutGroups.forEach((calloutGroup) => {
    if (String(calloutGroup.getAttribute('data-callout-type') || '').trim().toLowerCase() === 'custom') {
      return;
    }

    const pin = normalizedPins.find((candidatePin) => doesCalloutGroupMatchPin(calloutGroup, candidatePin));
    const calloutLabel = calloutGroup.querySelector('.pin-callout-text')?.textContent || '';
    const calloutBaseColor = getDefaultPublicPinCalloutColor(pin, calloutLabel);
    const rect = calloutGroup.querySelector('rect.pin-callout-bubble[data-micropinouts-callout="1"]');
    const line = calloutGroup.querySelector('.pin-callout-line');
    const text = calloutGroup.querySelector('.pin-callout-text');
    const anchor = calloutGroup.querySelector('.pin-callout-anchor');

    setSvgStyle(rect, 'fill', '');
    setSvgStyle(rect, 'stroke', calloutBaseColor);
    setSvgStyle(line, 'stroke', calloutBaseColor);
    setSvgStyle(text, 'fill', calloutBaseColor);
    setSvgStyle(anchor, 'stroke', calloutBaseColor);
    setSvgStyle(anchor, 'fill', '#ffffff');
  });

  return true;
};
